// src/stores/caseStore.ts
import { useCaseChannelSubscription } from '@/composables/useChannelSubscription'
import { useCaseDocumentProcessing } from '@/composables/useDocumentProcessing'
import {
  AiAugmentedByMapperKeysEnum,
  type CustomerCase as Case,
  type CasesPartialUpdateData,
  CasesService,
  type CustomerAppointment,
  type CustomerCaseData,
  type CustomerCaseDataChange,
  type CustomerEntity,
  type CustomerEntityWithRelationship,
  type Document,
  DocumentsService,
  EntitiesService,
  EntityTypeEnum,
  type GenericDataMapper,
  type LawData,
  RelationshipTypeEnum
} from '@/types/generated'
import { OpenAPI } from '@/types/generated/core/OpenAPI'
import { request as __request } from '@/types/generated/core/request'
import { WebSocketMessageType } from '@/utils/channels/types'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { emitter } from './eventEmitter'
import { useDataMapperStore } from '@/store/dataMapperStore'
import { useUserStore } from '@/store/userStore'

interface UploadedDocumentRef {
  uuid: string
  title: string
  filename: string
  url: string
  created_at: string
  is_deleted: boolean
}

// Define the store function
export const useCaseStore = defineStore('caseStore', () => {
  const caseRecord = ref<Case | null>(null)
  const appointments = ref<CustomerAppointment[]>([])
  const appointmentProposals = ref<any[]>([])
  const documents = ref<Document[]>([])
  const isLoadingCase = ref<boolean>(false)
  const isLoadingEntities = ref<boolean>(false)
  const entitiesTotalCount = ref<number>(0)
  const entitiesHasMore = ref<boolean>(false)
  const isLoadingAppointments = ref<boolean>(false)
  const isLoadingAppointmentProposals = ref<boolean>(false)
  const isLoadingDocuments = ref<boolean>(false)
  const isSavingCase = ref<boolean>(false)
  const caseLoadError = ref<Error | null>(null)
  const isCreatingCase = ref<boolean>(false)
  const uploadedDocumentsMap = ref<Map<string, UploadedDocumentRef>>(new Map())
  const entitySourceDocumentMap = ref<Map<string, string>>(new Map())

  const dataMapperStore = useDataMapperStore()

  // Centralized WebSocket subscription for the entire case
  const caseUuid = computed(() => {
    if (isCreatingCase.value) return ''
    return caseRecord.value?.uuid || ''
  })
  const caseWebSocketSubscription = useCaseChannelSubscription(caseUuid, {
    disableAutoLifecycle: true // Manual control - subscribe when case loads, unsubscribe when case changes
  })

  // Use the document processing composable for case documents
  const documentProcessor = useCaseDocumentProcessing(caseRecord)

  // Set up case refresh callback for when documents complete processing
  documentProcessor.setRefreshCallback(async (documentUuid?: string) => {
    if (caseRecord.value?.uuid) {
      const caseUuid = caseRecord.value.uuid

      // Fetch updated documents
      await fetchCaseDocuments(caseUuid)
      if (caseRecord.value?.uuid !== caseUuid) return

      try {
        const updatedCase = await CasesService.casesRetrieve({ uuid: caseUuid })
        if (caseRecord.value?.uuid !== caseUuid) return

        if (updatedCase && caseRecord.value) {
          if ('data' in updatedCase && updatedCase.data) {
            caseRecord.value = {
              ...caseRecord.value,
              data: {
                ...updatedCase.data,
                entities: caseRecord.value.data.entities
              },
              documents: caseRecord.value.documents
            } as Case
          }
        }
        if (caseRecord.value?.uuid === caseUuid && caseRecord.value?.data?.uuid) {
          await refreshCaseEntities(caseRecord.value.data.uuid)
        }
      } catch (error) {
        console.error('[CaseDetailStore] Error fetching case data after document processing', {
          caseUuid,
          documentUuid,
          error
        })
      }
    }
  })

  // Coalesce CASE_UPDATE bursts. A single agent commit often emits several
  // CaseDataChange rows (law_field + law_topic + clerks); refetching once is
  // enough. fetchCase already guards against torn writes via latestFetchCaseToken.
  let caseDataRefetchTimer: ReturnType<typeof setTimeout> | null = null
  const CASE_UPDATE_DEBOUNCE_MS = 250

  const scheduleCaseDataRefetch = (uuid: string) => {
    if (caseDataRefetchTimer) clearTimeout(caseDataRefetchTimer)
    caseDataRefetchTimer = setTimeout(() => {
      caseDataRefetchTimer = null
      if (caseRecord.value?.uuid !== uuid) return
      fetchCase(uuid).catch((error) => {
        console.error('[CaseDetailStore] Error refetching case after CASE_UPDATE', {
          caseUuid: uuid,
          error
        })
      })
    }, CASE_UPDATE_DEBOUNCE_MS)
  }

  watch(
    () => caseRecord.value?.uuid,
    () => {
      if (caseDataRefetchTimer) {
        clearTimeout(caseDataRefetchTimer)
        caseDataRefetchTimer = null
      }
    }
  )

  // Watch for case-related messages from case channel
  watch(caseWebSocketSubscription.lastMessage, async (message) => {
    if (!message) return

    if (message.type === WebSocketMessageType.CASE_DOCUMENT_PROCESSING) {
      // Handle case document processing messages for case documents
      documentProcessor.handleCaseDocumentProcessingMessage(message)

      // Also update the document in caseRecord.documents directly for immediate UI update
      if (message.data?.document_uuid && caseRecord.value?.documents) {
        const docIndex = caseRecord.value.documents.findIndex(
          (d) => d.uuid === message.data.document_uuid
        )
        if (docIndex !== -1) {
          const updatedDoc = {
            ...caseRecord.value.documents[docIndex],
            metadata_enrichment_is_finished: message.data.status === 'FINISHED',
            metadata_enrichment_error: message.data.status === 'FAILED' ? message.data.error : null
          }

          // Reassign the array to trigger reactivity
          caseRecord.value = {
            ...caseRecord.value,
            documents: [
              ...caseRecord.value.documents.slice(0, docIndex),
              updatedDoc,
              ...caseRecord.value.documents.slice(docIndex + 1)
            ]
          }
        }
      }

      if (message.data?.status === 'FINISHED' && caseRecord.value?.uuid) {
        const caseUuid = caseRecord.value.uuid
        try {
          await fetchCaseDocuments(caseUuid)
          if (caseRecord.value?.uuid !== caseUuid) return

          const updatedCase = await CasesService.casesRetrieve({ uuid: caseUuid })
          if (caseRecord.value?.uuid !== caseUuid) return

          if (updatedCase && caseRecord.value) {
            if ('data' in updatedCase && updatedCase.data) {
              caseRecord.value = {
                ...caseRecord.value,
                data: {
                  ...updatedCase.data,
                  entities: caseRecord.value.data.entities
                },
                documents: caseRecord.value.documents
              } as Case
            }
          }
          if (caseRecord.value?.uuid === caseUuid && caseRecord.value?.data?.uuid) {
            await refreshCaseEntities(caseRecord.value.data.uuid)
          }
        } catch (error) {
          console.error('[CaseDetailStore] Error fetching case data after document finished', {
            caseUuid,
            documentUuid: message.data?.document_uuid,
            error
          })
        }
      }
    }

    if (
      message.type === WebSocketMessageType.CASE_UPDATE &&
      message.data?.case_uuid &&
      caseRecord.value?.uuid === message.data.case_uuid &&
      message.data.change_source !== 'USER'
    ) {
      scheduleCaseDataRefetch(message.data.case_uuid)
    }

    if (
      message.type === WebSocketMessageType.CASE_DUPLICATE_DETECTED &&
      message.data?.case_uuid &&
      caseRecord.value?.uuid === message.data.case_uuid
    ) {
      const caseUuid = message.data.case_uuid
      // Update immediately so the duplicate banner can show without waiting for refetch
      if (message.data?.has_pending_duplicate_matches !== false) {
        caseRecord.value = {
          ...caseRecord.value!,
          has_pending_duplicate_matches: true
        } as Case
      }
      try {
        const updatedCase = (await CasesService.casesRetrieve({ uuid: caseUuid })) as Case
        if (caseRecord.value?.uuid === caseUuid) {
          caseRecord.value = {
            ...updatedCase,
            data: {
              ...updatedCase.data,
              entities: caseRecord.value.data.entities
            }
          } as Case
        }
      } catch (error) {
        console.error('[CaseDetailStore] Error fetching case data after duplicate detected', {
          caseUuid,
          error
        })
      }
    }
  })

  // Only the latest fetchCase invocation may assign to caseRecord. Prevents
  // an older in-flight response from overwriting newer state after callers
  // opt out of coalescing via { force: true }.
  let latestFetchCaseToken = 0

  // Actions (asynchronous actions such as fetching data)
  async function fetchCase(uuid: string) {
    const token = ++latestFetchCaseToken
    const result = (await CasesService.casesRetrieve({ uuid: uuid })) as Case
    if (token === latestFetchCaseToken) {
      // Preserve the locally-paginated entity list across same-case reloads —
      // the casesRetrieve endpoint always returns entities=[] (the dedicated
      // case-entities endpoint is the source of truth), so blindly assigning
      // `result` would unmount every rendered row.
      const sameCase = caseRecord.value?.uuid === uuid
      const previousEntities = sameCase ? (caseRecord.value?.data?.entities ?? []) : []
      caseRecord.value = sameCase
        ? ({
            ...result,
            data: { ...result.data, entities: previousEntities }
          } as Case)
        : result
    }
  }

  /**
   * Fetch only documents for a case and update the case record.
   *
   * :param uuid: The UUID of the case to fetch documents for
   * :return: None
   */
  async function fetchCaseDocuments(uuid: string) {
    const expectedUuid = caseRecord.value?.uuid
    if (!expectedUuid || expectedUuid !== uuid) return

    const response = await DocumentsService.documentsList({
      caseUuid: uuid
    })

    if (response.results && caseRecord.value?.uuid === expectedUuid) {
      caseRecord.value = {
        ...caseRecord.value,
        documents: response.results
      }
    }
  }

  const ENTITIES_PAGE_SIZE = 20
  let entitiesRequestToken = 0

  async function fetchCaseEntities(
    caseDataUuid: string,
    options?: {
      limit?: number
      offset?: number
      search?: string
      append?: boolean
      background?: boolean
    }
  ): Promise<{ count: number; hasMore: boolean } | undefined> {
    const expectedCaseUuid = caseRecord.value?.uuid
    if (!expectedCaseUuid) return

    const limit = options?.limit ?? ENTITIES_PAGE_SIZE
    const offset = options?.offset ?? 0
    const append = options?.append ?? false
    // Background refreshes keep the existing list visible — only flip the
    // loading flag when there are no entities to show yet.
    const isBackground = options?.background ?? (caseRecord.value?.data?.entities?.length ?? 0) > 0

    const requestToken = ++entitiesRequestToken
    if (!isBackground) {
      isLoadingEntities.value = true
    }
    try {
      const query: Record<string, any> = {
        case_data: caseDataUuid,
        limit,
        offset
      }
      if (options?.search) {
        query.search = options.search
      }

      const response = (await __request(OpenAPI, {
        method: 'GET',
        url: '/api/v3/entities/case-entities/',
        query
      })) as { count: number; results: CustomerEntityWithRelationship[] }

      if (requestToken !== entitiesRequestToken) return
      if (caseRecord.value?.uuid !== expectedCaseUuid) return

      if (response.results && caseRecord.value) {
        const currentEntities = caseRecord.value.data.entities
        const newEntities = append ? [...currentEntities, ...response.results] : response.results

        const sameList =
          !append &&
          currentEntities.length === newEntities.length &&
          currentEntities.every((e, i) => e.uuid === newEntities[i].uuid)

        if (!sameList) {
          caseRecord.value = {
            ...caseRecord.value,
            data: {
              ...caseRecord.value.data,
              entities: newEntities
            }
          } as Case
        }

        entitiesTotalCount.value = response.count
        entitiesHasMore.value = offset + limit < response.count

        return { count: response.count, hasMore: entitiesHasMore.value }
      }
    } catch (error) {
      console.error('[CaseDetailStore] Error fetching case entities', {
        caseDataUuid,
        error
      })
    } finally {
      if (requestToken === entitiesRequestToken && !isBackground) {
        isLoadingEntities.value = false
      }
    }
  }

  async function refreshCaseEntities(caseDataUuid: string) {
    const currentCount = caseRecord.value?.data?.entities?.length ?? 0
    return fetchCaseEntities(caseDataUuid, {
      limit: Math.max(ENTITIES_PAGE_SIZE, currentCount),
      background: true
    })
  }

  async function fetchAppointments(uuid: string) {
    isLoadingAppointments.value = true
    try {
      // The endpoint returns an array directly, not a paginated response
      const response = await CasesService.casesAppointmentsList({ uuid })
      appointments.value = response as any as CustomerAppointment[]
    } catch (error) {
      console.error('Error fetching appointments:', error)
      appointments.value = []
    } finally {
      isLoadingAppointments.value = false
    }
  }

  async function fetchDocuments(uuid: string) {
    isLoadingDocuments.value = true
    try {
      // The endpoint returns an array directly, not a paginated response
      const response = await CasesService.casesDocumentsList({ uuid })
      documents.value = response as any as Document[]

      // Also update caseRecord.documents for document processing composable
      if (caseRecord.value) {
        caseRecord.value = {
          ...caseRecord.value,
          documents: documents.value
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      documents.value = []
      // Clear documents from caseRecord on error
      if (caseRecord.value) {
        caseRecord.value = {
          ...caseRecord.value,
          documents: []
        }
      }
    } finally {
      isLoadingDocuments.value = false
    }
  }

  async function fetchAppointmentProposals(uuid: string) {
    isLoadingAppointmentProposals.value = true
    try {
      // The endpoint returns an array directly, not a paginated response
      const response = await CasesService.casesAppointmentProposalsRetrieve({ uuid })
      appointmentProposals.value = response as any
    } catch (error) {
      console.error('Error fetching appointment proposals:', error)
      appointmentProposals.value = []
    } finally {
      isLoadingAppointmentProposals.value = false
    }
  }

  async function fetchCaseWithErrorHandling(uuid: string) {
    isLoadingCase.value = true
    caseLoadError.value = null

    try {
      const sameCase = caseRecord.value?.uuid === uuid
      const previousEntities = sameCase ? (caseRecord.value?.data?.entities ?? []) : []
      if (!sameCase) {
        entitiesTotalCount.value = 0
        entitiesHasMore.value = false
      }
      const fetchedCase = (await CasesService.casesRetrieve({ uuid: uuid })) as Case
      caseRecord.value = {
        ...fetchedCase,
        data: {
          ...fetchedCase.data,
          entities: previousEntities
        }
      } as Case

      if (caseRecord.value?.data?.uuid) {
        await fetchCaseEntities(caseRecord.value.data.uuid, { background: sameCase })
      }

      return caseRecord.value
    } catch (error) {
      console.error('Error fetching case:', error)
      caseLoadError.value = error instanceof Error ? error : new Error(String(error))
      throw error
    } finally {
      isLoadingCase.value = false
    }
  }

  const lawSpecificMappers = computed<GenericDataMapper[]>(() => {
    if (!caseRecord.value?.data) return []
    return dataMapperStore.mappers.filter(
      (mapper) =>
        mapper.is_law_specific_field && mapper.law_field !== undefined && mapper.law_field !== null
    )
  })

  const nonEmptyLawSpecificMappers = computed<GenericDataMapper[]>(() => {
    return lawSpecificMappers.value.filter((mapper) => {
      const [parent, field] = mapper.key.split('.') as [keyof LawData, string]
      // Type assertion since we know parent will be 'law' for law specific fields
      const parentData = caseRecord.value?.data?.[parent as 'law']
      if (!parentData || typeof parentData !== 'object') return false

      // Access the nested field
      const value = (parentData as any)[field]

      // Check for empty values
      if (value === undefined || value === null || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      if (typeof value === 'object' && Object.keys(value).length === 0) return false

      return true
    })
  })

  /**
   * Ensure WebSocket is subscribed for the current case.
   * Safe to call multiple times - will only subscribe if needed.
   */
  function ensureWebSocketSubscribed() {
    if (isCreatingCase.value) return
    if (caseRecord.value?.uuid && !caseWebSocketSubscription.isSubscribed.value) {
      void caseWebSocketSubscription.subscribe().catch(() => {
        // Failure surfaced via composable lastError / connectionStatus
      })
    }
  }

  /**
   * Unsubscribe from WebSocket.
   * Called when navigating away from all case detail routes.
   */
  function disconnectWebSocket() {
    caseWebSocketSubscription.unsubscribe()
  }

  function resetCreationState() {
    isCreatingCase.value = false
    caseRecord.value = null
    uploadedDocumentsMap.value.clear()
    entitySourceDocumentMap.value.clear()
  }

  // Coalesce concurrent loads for the same uuid so an older response can't
  // overwrite fresher state assigned by a newer load (race between mount,
  // broadcast-triggered refresh, explicit refresh, etc.). Tracks both a "fast"
  // promise (fetchCase only) and a "full" promise (also awaits related data),
  // so a caller asking for waitForRelatedData:true that coalesces onto a
  // running fast load still gets the stronger contract.
  interface InFlightLoad {
    fast: Promise<void>
    full: Promise<void>
  }
  const inFlightLoads = new Map<string, InFlightLoad>()

  async function loadCase(
    uuid: string,
    options?: { waitForRelatedData?: boolean; force?: boolean }
  ) {
    const wantsFull = options?.waitForRelatedData === true

    // force:true bypasses coalescing — used for post-mutation refreshes where
    // an already-running pre-mutation fetch would otherwise return stale data.
    if (!options?.force) {
      const existing = inFlightLoads.get(uuid)
      if (existing) return wantsFull ? existing.full : existing.fast
    }

    // Synchronous setup must happen here (not inside the async body below) so
    // subscribers of the isLoading* flags see the transition immediately.
    if (caseRecord.value?.uuid && caseRecord.value.uuid !== uuid) {
      caseWebSocketSubscription.unsubscribe()
    }
    documentProcessor.clearDocumentProcessingStates()
    ensureWebSocketSubscribed()

    // Same-case reloads keep the existing entity list visible — flipping
    // isLoadingEntities here would unmount the rendered rows and produce a
    // flicker on every websocket-driven or post-mutation refresh.
    const isSameCaseReload =
      caseRecord.value?.uuid === uuid && (caseRecord.value?.data?.entities?.length ?? 0) > 0
    if (!isSameCaseReload) {
      isLoadingEntities.value = true
    }
    isLoadingAppointments.value = true
    isLoadingAppointmentProposals.value = true
    isLoadingDocuments.value = true

    // Fetch the mapper schema in the background — case detail must not wait on it.
    void dataMapperStore.fetchMappers().catch((error) => {
      console.error('[CaseDetailStore] Failed to load data mappers:', error)
    })

    const fast = (async () => {
      try {
        await fetchCase(uuid)
      } catch (error) {
        console.error('Error loading case:', error)
        throw error
      }
    })()

    const full = fast.then(() =>
      Promise.all([
        fetchCaseEntities(caseRecord.value?.data?.uuid as string, {
          background: isSameCaseReload
        }),
        fetchAppointments(uuid),
        fetchAppointmentProposals(uuid),
        fetchDocuments(uuid)
      ]).then(() => undefined)
    )

    // Preserve the original fire-and-forget logging for related-data failures
    // so they aren't silenced when only fast callers consume the load, and so
    // full always has a handler attached (avoids unhandledRejection noise).
    // Full callers that await `full` still see rejections propagate.
    full.catch((error) => {
      console.error('Error loading case related data:', error)
    })

    const entry: InFlightLoad = { fast, full }
    inFlightLoads.set(uuid, entry)

    void full.finally(() => {
      // A force:true caller may have replaced this entry with a newer load;
      // only clear the map when the current entry is still this one.
      if (inFlightLoads.get(uuid) === entry) {
        inFlightLoads.delete(uuid)
      }
    })

    return wantsFull ? full : fast
  }

  // Helper function to optimistically remove documents from the case
  function removeDocumentsOptimistically(documentUuids: string[]) {
    // Remove documents from the separate documents array
    documents.value = documents.value.filter((doc) => !documentUuids.includes(doc.uuid))
  }

  function updateEntity(entityUuid: string, updates: Record<string, any>) {
    if (!caseRecord.value?.data.entities) return

    const entityIndex = caseRecord.value.data.entities.findIndex(
      (e) => e.entity?.uuid === entityUuid
    )
    if (entityIndex === -1) return

    const entity = caseRecord.value.data.entities[entityIndex].entity!

    Object.assign(entity, updates)

    if (isCreatingCase.value && entity.changes) {
      const userStore = useUserStore()
      const currentUser = userStore.user

      const userForChange = currentUser
        ? {
            uuid: currentUser.uuid,
            email: currentUser.email,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            avatar: currentUser.avatar
          }
        : null

      Object.keys(updates).forEach((fieldName) => {
        const mapperKey = `entities.field.${fieldName}`
        const newValue = updates[fieldName]

        // Find existing change for this field
        const existingChangeIndex = entity.changes!.findIndex(
          (change) => change.entities_mapper_key === mapperKey
        )

        if (existingChangeIndex !== -1) {
          // Update existing change to be USER-sourced
          const existingChange = entity.changes![existingChangeIndex]
          entity.changes![existingChangeIndex] = {
            ...existingChange,
            new_value: String(newValue || ''),
            change_source: 'USER' as any,
            ai_augmentation: null,
            user: userForChange as any
          }
        } else {
          // Create new USER change entry
          entity.changes!.push({
            uuid: 'temp-change-' + crypto.randomUUID(),
            created_at: new Date().toISOString(),
            entities_mapper_key: mapperKey as any,
            old_value: '',
            new_value: String(newValue || ''),
            change_source: 'USER' as any,
            questionnaire_submission: null,
            user: userForChange as any,
            ai_augmentation: null
          })
        }
      })
    }
  }

  function registerUploadedDocument(document: any) {
    if (document?.uuid) {
      const plainDocument: UploadedDocumentRef = {
        uuid: document.uuid,
        title: document.original_filename || 'Document',
        filename: document.original_filename || 'document.pdf',
        url: document.file,
        created_at: document.created_at,
        is_deleted: false
      }
      uploadedDocumentsMap.value.set(document.uuid, plainDocument)
    }
  }

  function batchAddEntities(
    entities: Array<{
      entity: Partial<CustomerEntity>
      relationshipType: RelationshipTypeEnum
      sourceDocumentUuid?: string
    }>
  ) {
    if (!isCreatingCase.value || !caseRecord.value?.data) return

    const newEntities = entities.map(({ entity, relationshipType, sourceDocumentUuid }) => {
      const isUnique = relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT

      // Create mock AI augmentation changes for fields that have data
      const mockChanges: any[] = []
      if (sourceDocumentUuid) {
        // Get the full document object from the map
        const document = uploadedDocumentsMap.value.get(sourceDocumentUuid)

        // Only create AI augmentation if we have the full document object
        if (!document) {
          console.warn(`Document ${sourceDocumentUuid} not found in uploadedDocumentsMap`)
        }

        const mockAiAugmentation = document
          ? {
              uuid: 'temp-augmentation-' + crypto.randomUUID(),
              augmentation_variant: 'DOCUMENT ANALYSIS',
              document: document
            }
          : null

        if (mockAiAugmentation) {
          const fieldMappings: Array<[string, any]> = [
            ['entity_type', entity.entity_type],
            ['title', entity.title],
            ['specific_title', entity.specific_title],
            ['first_name', entity.first_name],
            ['last_name', entity.last_name],
            ['company_name', entity.company_name],
            ['company_legal_form', entity.company_legal_form],
            ['institution_name', entity.institution_name],
            ['phone', entity.phone],
            ['email', entity.email],
            ['date_of_birth', entity.date_of_birth],
            ['street', entity.street],
            ['street_number', entity.street_number],
            ['address_line_2', entity.address_line_2],
            ['zip', entity.zip],
            ['city', entity.city],
            ['legal_insurance', entity.legal_insurance],
            ['legal_insurance_number', entity.legal_insurance_number],
            ['legal_insurance_name', entity.legal_insurance_name]
          ]

          fieldMappings.forEach(([fieldName, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              mockChanges.push({
                uuid: 'temp-change-' + crypto.randomUUID(),
                created_at: new Date().toISOString(),
                entities_mapper_key: `entities.field.${fieldName}`,
                old_value: '',
                new_value: String(value),
                change_source: 'AI',
                questionnaire_submission: null,
                user: null,
                ai_augmentation: mockAiAugmentation
              })
            }
          })
        }
      }

      const entityInnerUuid = 'temp-entity-inner-' + crypto.randomUUID()

      if (sourceDocumentUuid) {
        entitySourceDocumentMap.value.set(entityInnerUuid, sourceDocumentUuid)
      }

      return {
        uuid: 'temp-entity-' + crypto.randomUUID(),
        entity: {
          uuid: entityInnerUuid,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_company: entity.entity_type === 'legal_person' ? 'True' : 'False',
          changes: mockChanges,
          proposed_changes: [],
          entity_type: entity.entity_type || EntityTypeEnum.NATURAL_PERSON,
          title: entity.title || '',
          specific_title: entity.specific_title || '',
          first_name: entity.first_name || '',
          last_name: entity.last_name || '',
          company_name: entity.company_name || '',
          company_legal_form: entity.company_legal_form || '',
          institution_name: entity.institution_name || '',
          phone: entity.phone || '',
          email: entity.email || '',
          date_of_birth: entity.date_of_birth || null,
          street: entity.street || '',
          street_number: entity.street_number || '',
          address_line_2: entity.address_line_2 || '',
          zip: entity.zip || '',
          city: entity.city || '',
          legal_insurance: entity.legal_insurance || null,
          legal_insurance_number: entity.legal_insurance_number || '',
          legal_insurance_name: entity.legal_insurance_name || ''
        },
        relationships: [
          {
            uuid: 'temp-rel-' + crypto.randomUUID(),
            relationship_type: relationshipType,
            is_unique_per_case: isUnique,
            number: null
          }
        ]
      }
    })

    // Check if any new entity is a client or opponent
    const hasNewClient = newEntities.some((e) =>
      e.relationships.some((r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT)
    )
    const hasNewOpponent = newEntities.some((e) =>
      e.relationships.some((r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT)
    )

    let existingEntities = caseRecord.value.data.entities

    // if adding a new client, drop only empty placeholder clients. preserving
    // non-empty existing clients lets multi-file uploads layer data into the
    // store; the dedup endpoint then collapses the duplicates.
    if (hasNewClient) {
      existingEntities = existingEntities.filter((e) => {
        const isClient = e.relationships.some(
          (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
        )
        if (!isClient) return true

        const entity = e.entity
        const hasData =
          entity.first_name ||
          entity.last_name ||
          entity.company_name ||
          entity.institution_name ||
          entity.email ||
          entity.phone
        return hasData
      })
    }

    // If adding new opponents, remove existing empty opponents (only if they're empty/default)
    if (hasNewOpponent) {
      existingEntities = existingEntities.filter((e) => {
        const isOpponent = e.relationships.some(
          (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
        )
        if (!isOpponent) return true

        // Keep opponent if it has any non-empty fields
        const entity = e.entity
        const hasData =
          entity.first_name ||
          entity.last_name ||
          entity.company_name ||
          entity.institution_name ||
          entity.email ||
          entity.phone
        return hasData
      })
    }

    caseRecord.value = {
      ...caseRecord.value,
      data: {
        ...caseRecord.value.data,
        entities: [...existingEntities, ...newEntities]
      }
    } as Case

    return newEntities
  }

  async function addEntity(
    entity: CustomerEntity,
    relationshipType: RelationshipTypeEnum = RelationshipTypeEnum.ENTITIES_OTHER
  ) {
    if (isCreatingCase.value) {
      const isUnique = relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
      const newEntityWithRelationship: CustomerEntityWithRelationship = {
        uuid: 'temp-entity-' + crypto.randomUUID(),
        entity: {
          ...entity,
          uuid: 'temp-entity-inner-' + crypto.randomUUID(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_company: entity.entity_type === 'legal_person' ? 'True' : 'False',
          changes: [],
          proposed_changes: []
        },
        relationships: [
          {
            uuid: 'temp-rel-' + crypto.randomUUID(),
            relationship_type: relationshipType,
            is_unique_per_case: isUnique,
            number: null
          }
        ]
      }
      if (caseRecord.value?.data?.entities) {
        caseRecord.value = {
          ...caseRecord.value,
          data: {
            ...caseRecord.value.data,
            entities: [...caseRecord.value.data.entities, newEntityWithRelationship]
          }
        } as Case
      }
      return newEntityWithRelationship
    }

    const response = await EntitiesService.entitiesCreate({
      requestBody: entity
    })
    const relationshipResponse = await EntitiesService.entitiesAddCaseDataRelationshipCreate({
      uuid: response.uuid,
      requestBody: {
        case_data_uuid: caseRecord.value?.data.uuid as string,
        relationship_type: relationshipType
      }
    })
    await refreshCaseEntities(caseRecord.value?.data.uuid as string)
    return relationshipResponse
  }

  async function deleteEntity(entityRelationShip: CustomerEntityWithRelationship) {
    if (!entityRelationShip || !entityRelationShip.entity || !entityRelationShip.entity.uuid) return

    if (isCreatingCase.value) {
      if (caseRecord.value?.data?.entities) {
        caseRecord.value = {
          ...caseRecord.value,
          data: {
            ...caseRecord.value.data,
            entities: caseRecord.value.data.entities.filter(
              (e) => e.uuid !== entityRelationShip.uuid
            )
          }
        } as Case
      }
      return
    }

    // Check if the entity has relationships
    if (!entityRelationShip.relationships || entityRelationShip.relationships.length === 0) {
      await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
        uuid: entityRelationShip.entity.uuid,
        requestBody: {
          case_data_uuid: caseRecord.value?.data.uuid as string,
          remove_case_data_association_if_no_relationships_exist: true
        }
      })
    } else {
      for (const relationship of entityRelationShip.relationships) {
        await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
          uuid: entityRelationShip.entity.uuid,
          requestBody: {
            case_data_uuid: caseRecord.value?.data.uuid as string,
            relationship_uuid: relationship.uuid as string,
            remove_case_data_association_if_no_relationships_exist: true
          }
        })
      }
    }

    // Then we load the case again to get fresh data
    await loadCase(caseRecord.value?.uuid as string)
  }

  // Local-only removal during case-creation: filters the draft entities by
  // wrapper uuid. Used by the dedup endpoint flow to collapse duplicates;
  // no API call because the case doesn't exist yet.
  function removeEntitiesFromCreation(storeEntityUuids: string[]) {
    if (!isCreatingCase.value || !caseRecord.value?.data?.entities) return
    if (storeEntityUuids.length === 0) return

    const toRemove = new Set(storeEntityUuids)
    caseRecord.value = {
      ...caseRecord.value,
      data: {
        ...caseRecord.value.data,
        entities: caseRecord.value.data.entities.filter((e) => !toRemove.has(e.uuid as string))
      }
    } as Case
  }

  async function bulkDeleteEntities(entityRelationships: CustomerEntityWithRelationship[]) {
    if (!entityRelationships || entityRelationships.length === 0 || !caseRecord.value?.data.uuid)
      return

    const entityRelationshipUuids = entityRelationships
      .map((er) => er.uuid)
      .filter(Boolean) as string[]

    if (entityRelationshipUuids.length === 0) return

    // Use any entity UUID for the endpoint (we'll use the first one)
    // The endpoint will process all entity_relationship_uuids from the request body
    const firstEntityUuid = entityRelationships[0]?.entity?.uuid
    if (!firstEntityUuid) return

    await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
      uuid: firstEntityUuid,
      requestBody: {
        case_data_uuid: caseRecord.value.data.uuid,
        entity_relationship_uuids: entityRelationshipUuids,
        remove_case_data_association_if_no_relationships_exist: true
      }
    })

    // Reload the case to get fresh data
    await loadCase(caseRecord.value.uuid as string)
  }

  async function updateOpponentField(field: string, value: any) {
    if (!caseRecord.value || !caseRecord.value.data || !caseRecord.value.data.opponent) return

    // Update local state
    caseRecord.value = {
      ...caseRecord.value,
      data: {
        ...caseRecord.value.data,
        opponent: {
          ...caseRecord.value.data.opponent,
          [field]: value
        }
      }
    }

    // Persist changes to the backend
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          data: {
            opponent: {
              [field]: value
            }
          }
        }
      })
      return true
    } catch (error) {
      console.error(`Failed to update opponent field ${field}:`, error)
      return false
    }
  }

  async function updateClientField(field: string, value: any) {
    if (!caseRecord.value || !caseRecord.value.data || !caseRecord.value.data.client) return

    // Update local state
    caseRecord.value = {
      ...caseRecord.value,
      data: {
        ...caseRecord.value.data,
        client: {
          ...caseRecord.value.data.client,
          [field]: value
        }
      }
    }

    // Persist changes to the backend
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          data: {
            client: {
              [field]: value
            }
          }
        }
      })
      return true
    } catch (error) {
      console.error(`Failed to update client field ${field}:`, error)
      return false
    }
  }

  function hasOpponentFieldAISource(field: string): boolean {
    // if (!caseRecord.value?.data?.opponent?.changes) return false

    // const fieldChanges = caseRecord.value.data.opponent.changes.filter(
    //   (change: CustomerCaseDataChange) => {
    //     const mapperKey = `opponent.${field}`
    //     return change.key === mapperKey || change.field_name === field
    //   }
    // )

    // if (fieldChanges.length === 0) return false

    // // Sort by creation date to get the latest change
    // const latestChange = fieldChanges.sort(
    //   (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    // )[0]

    // return latestChange.source === 'AI' // Assuming 'AI' is the source value
    return false
  }

  function updateCaseChanges(changes: CustomerCaseDataChange[]) {
    if (caseRecord.value) {
      caseRecord.value = {
        ...caseRecord.value,
        data: {
          ...caseRecord.value.data,
          changes
        }
      }
    }
  }

  function getDataWithMapperKey(mapperKey: AiAugmentedByMapperKeysEnum, entityUuid?: string) {
    // TODO: implement this for the entities using the entityUuid
    if (!mapperKey) {
      console.warn('Attempted to get data with undefined mapper key')
      return undefined
    }
    const parts = (mapperKey || '').split('.')
    if (parts.length !== 2) {
      console.warn('Invalid mapper key format:', mapperKey)
      return undefined
    }

    const [parent, field] = parts
    // Safely access the data using the parent key first
    const parentData = caseRecord.value?.data?.[parent as keyof CustomerCaseData]

    // Then access the field on the parent data if it exists
    if (parentData && typeof parentData === 'object') {
      return (parentData as any)[field]
    }

    return undefined
  }

  function updateDataWithMapperKey(
    mapperKey: AiAugmentedByMapperKeysEnum,
    value: any,
    entityUuid?: string
  ) {
    // TODO: implement this for the entities using the entityUuid
  }

  async function saveDataWithMapperKey(
    mapperKey: AiAugmentedByMapperKeysEnum,
    value: any,
    entityUuid?: string
  ) {
    isSavingCase.value = true
    try {
      updateDataWithMapperKey(mapperKey, value)
      const parts = mapperKey.split('.')
      if (parts.length !== 2) {
        console.warn('Invalid mapper key format:', mapperKey)
        return
      }

      const [parent, field] = parts

      // Check if caseRecord exists
      if (!caseRecord.value) {
        console.warn('Cannot save data: caseRecord is null')
        return
      }

      // Check if there are any actual changes on that field
      const parentData = caseRecord.value.data?.[parent as keyof CustomerCaseData]
      if (parentData && typeof parentData === 'object' && (parentData as any)[field] === value) {
        return
      }

      // Create the data object with the nested structure
      const dataToUpdate = {
        data: { [parent]: { [field]: value } }
      }

      // Use type assertion to match the expected CasesPartialUpdateData type
      const updateData = {
        uuid: caseRecord.value.uuid as string,
        requestBody: dataToUpdate
      } as unknown as CasesPartialUpdateData

      const result: Case = (await CasesService.casesPartialUpdate(updateData)) as Case

      // Update the case changes through the store
      if (result.data.changes) {
        updateCaseChanges(result.data.changes)
      }

      // Update the local state directly to ensure UI is in sync
      if (caseRecord.value && caseRecord.value.data) {
        // Ensure the parent object exists
        if (!caseRecord.value.data[parent as keyof CustomerCaseData]) {
          ;(caseRecord.value.data as any)[parent] = {}
        }

        // Update the field value
        const parentObj = caseRecord.value.data[parent as keyof CustomerCaseData]
        if (parentObj && typeof parentObj === 'object') {
          ;(parentObj as any)[field] = value
        }

        // Emit an event to notify components that data has been updated
        emitter.emit('data-updated', {
          key: parent,
          mapperKey: mapperKey,
          value: value
        })
      }
    } finally {
      isSavingCase.value = false
    }
  }

  /**
   * Bulk partial update of DataWithMapperKeys
   * @param updates Object where key is the mapperKey (e.g., 'law.field') and value is the new value
   */
  async function bulkUpdateDataWithMapperKeys(updates: Record<string, any>) {
    if (!caseRecord.value) {
      console.warn('Cannot bulk update: caseRecord is null')
      return
    }
    isSavingCase.value = true
    try {
      // Group updates by parent
      const dataToUpdate: Record<string, Record<string, any>> = {}
      Object.entries(updates).forEach(([mapperKey, value]) => {
        const parts = mapperKey.split('.')
        if (parts.length !== 2) {
          console.warn('Invalid mapper key format:', mapperKey)
          return
        }
        const [parent, field] = parts
        if (!dataToUpdate[parent]) dataToUpdate[parent] = {}
        dataToUpdate[parent][field] = value
      })
      const updateData = {
        uuid: caseRecord.value.uuid as string,
        requestBody: { data: dataToUpdate }
      } as unknown as CasesPartialUpdateData
      const result: Case = (await CasesService.casesPartialUpdate(updateData)) as Case
      // Update the case changes through the store
      if (result.data.changes) {
        updateCaseChanges(result.data.changes)
      }
      // Update the local state for each field
      Object.entries(updates).forEach(([mapperKey, value]) => {
        const parts = mapperKey.split('.')
        if (parts.length !== 2) return
        const [parent, field] = parts
        if (!caseRecord.value!.data[parent as keyof CustomerCaseData]) {
          ;(caseRecord.value!.data as any)[parent] = {}
        }
        const parentObj = caseRecord.value!.data[parent as keyof CustomerCaseData]
        if (parentObj && typeof parentObj === 'object') {
          ;(parentObj as any)[field] = value
        }
        emitter.emit('data-updated', {
          key: parent,
          mapperKey,
          value
        })
      })
    } finally {
      isSavingCase.value = false
    }
  }

  function addEntityRelationshipDuringCreation(
    entityUuid: string,
    relationshipType: RelationshipTypeEnum
  ) {
    if (!caseRecord.value?.data?.entities) return

    const entityWithRelationship = caseRecord.value.data.entities.find(
      (e) => e.entity?.uuid === entityUuid
    )
    if (!entityWithRelationship) return

    // Check if relationship already exists
    const existingRelationship = entityWithRelationship.relationships.find(
      (r) => r.relationship_type === relationshipType
    )
    if (existingRelationship) return

    // Create new relationship
    const newRelationship = {
      uuid: 'temp-relationship-' + crypto.randomUUID(),
      relationship_type: relationshipType,
      case_data: caseRecord.value.data.uuid as string
    }

    entityWithRelationship.relationships.push(newRelationship as any)
  }

  function removeEntityRelationshipDuringCreation(entityUuid: string, relationshipUuid: string) {
    if (!caseRecord.value?.data?.entities) return

    const entityWithRelationship = caseRecord.value.data.entities.find(
      (e) => e.entity?.uuid === entityUuid
    )
    if (!entityWithRelationship) return

    const relationshipIndex = entityWithRelationship.relationships.findIndex(
      (r) => r.uuid === relationshipUuid
    )
    if (relationshipIndex === -1) return

    entityWithRelationship.relationships.splice(relationshipIndex, 1)
  }

  function switchClientDuringCreation(
    newClientEntityUuid: string,
    action: 'add' | 'remove' | 'addOpponent',
    selectedEntityUuid?: string
  ) {
    if (!caseRecord.value?.data?.entities) return

    // Find current client entity
    const currentClientEntity = caseRecord.value.data.entities.find((entity) =>
      entity.relationships.some((r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT)
    )

    // Find new client entity
    const newClientEntity = caseRecord.value.data.entities.find(
      (e) => e.entity?.uuid === newClientEntityUuid
    )

    if (!newClientEntity) return

    if (action === 'addOpponent' && selectedEntityUuid) {
      // Find the entity being converted to opponent (should be the current client)
      const selectedEntity = caseRecord.value.data.entities.find(
        (e) => e.entity?.uuid === selectedEntityUuid
      )

      if (!selectedEntity || !currentClientEntity) return

      // Verify that the selected entity is indeed the current client
      if (selectedEntity.entity.uuid !== currentClientEntity.entity.uuid) {
        console.error('Selected entity is not the current client')
        return
      }

      // Remove client relationship from current client
      const clientRelationship = currentClientEntity.relationships.find(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
      if (clientRelationship) {
        removeEntityRelationshipDuringCreation(
          currentClientEntity.entity.uuid,
          clientRelationship.uuid as string
        )
      }

      // Add opponent relationship to the same entity (now ex-client)
      addEntityRelationshipDuringCreation(
        currentClientEntity.entity.uuid,
        RelationshipTypeEnum.ENTITIES_OPPONENT
      )

      // If new client is currently an opponent, remove opponent relationship
      // (an entity cannot be both client and opponent)
      const newClientOpponentRelationship = newClientEntity.relationships.find(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
      if (newClientOpponentRelationship) {
        removeEntityRelationshipDuringCreation(
          newClientEntity.entity.uuid,
          newClientOpponentRelationship.uuid as string
        )
      }

      // Add client relationship to new client
      addEntityRelationshipDuringCreation(
        newClientEntity.entity.uuid,
        RelationshipTypeEnum.ENTITIES_CLIENT
      )
    } else {
      // For 'add' or 'remove' actions
      // Remove client relationship from current client
      if (currentClientEntity) {
        const clientRelationship = currentClientEntity.relationships.find(
          (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
        )
        if (clientRelationship) {
          removeEntityRelationshipDuringCreation(
            currentClientEntity.entity.uuid,
            clientRelationship.uuid as string
          )
        }
      }

      // If new client is currently an opponent, remove opponent relationship
      const opponentRelationship = newClientEntity.relationships.find(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
      if (opponentRelationship) {
        removeEntityRelationshipDuringCreation(
          newClientEntity.entity.uuid,
          opponentRelationship.uuid as string
        )
      }

      // Add client relationship to new client
      addEntityRelationshipDuringCreation(
        newClientEntity.entity.uuid,
        RelationshipTypeEnum.ENTITIES_CLIENT
      )
    }
  }

  const emailReminderInFlightFor = new Set<string>()
  async function setSendOutstandingDocumentEmailReminder(uuid: string, value: boolean) {
    if (!caseRecord.value || caseRecord.value.uuid !== uuid) return
    if (emailReminderInFlightFor.has(uuid)) return
    emailReminderInFlightFor.add(uuid)
    const serverBaseline = caseRecord.value.send_outstanding_document_email_reminder
    caseRecord.value.send_outstanding_document_email_reminder = value
    try {
      await CasesService.casesPartialUpdate({
        uuid,
        requestBody: { send_outstanding_document_email_reminder: value }
      })
    } catch (error) {
      if (caseRecord.value?.uuid === uuid) {
        caseRecord.value.send_outstanding_document_email_reminder = serverBaseline
      }
      throw error
    } finally {
      emailReminderInFlightFor.delete(uuid)
    }
  }

  // Return state, getters and actions
  return {
    caseRecord,
    appointments,
    appointmentProposals,
    documents,
    isLoadingCase,
    isLoadingEntities,
    entitiesTotalCount,
    entitiesHasMore,
    isLoadingAppointments,
    isLoadingAppointmentProposals,
    isLoadingDocuments,
    isSavingCase,
    caseLoadError,
    isCreatingCase,
    uploadedDocumentsMap,
    entitySourceDocumentMap,
    lawSpecificMappers,
    nonEmptyLawSpecificMappers,
    loadCase,
    fetchCaseEntities,
    fetchAppointments,
    fetchAppointmentProposals,
    fetchDocuments,
    removeDocumentsOptimistically,
    fetchCaseWithErrorHandling,
    updateEntity,
    addEntityRelationshipDuringCreation,
    removeEntityRelationshipDuringCreation,
    switchClientDuringCreation,
    updateCaseChanges,
    updateOpponentField,
    hasOpponentFieldAISource,
    updateClientField,
    getDataWithMapperKey,
    updateDataWithMapperKey,
    saveDataWithMapperKey,
    addEntity,
    batchAddEntities,
    registerUploadedDocument,
    deleteEntity,
    bulkDeleteEntities,
    removeEntitiesFromCreation,
    documentProcessor,
    bulkUpdateDataWithMapperKeys,
    setSendOutstandingDocumentEmailReminder,
    // WebSocket lifecycle management methods
    ensureWebSocketSubscribed,
    disconnectWebSocket,
    resetCreationState,
    // Expose centralized WebSocket connection for components (read-only, type-safe)
    caseWebSocket: {
      lastMessage: computed(() => caseWebSocketSubscription.lastMessage.value ?? null),
      isConnected: caseWebSocketSubscription.isConnected,
      connectionStatus: caseWebSocketSubscription.connectionStatus,
      subscribe: caseWebSocketSubscription.subscribe,
      unsubscribe: caseWebSocketSubscription.unsubscribe
    }
  }
})
